import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DuplicateNameDialog } from "@/components/event-details/add-guests/components/DuplicateNameDialog";
import { UserPlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
interface InlineGuestFormProps {
  eventId: string;
  onSuccess?: () => void;
}
export const InlineGuestForm = ({
  eventId,
  onSuccess
}: InlineGuestFormProps) => {
  const [firstName, setFirstName] = useState("");
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [existingNames, setExistingNames] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();

  // Fetch existing guest names when component mounts
  useEffect(() => {
    const fetchExistingGuests = async () => {
      if (!eventId) return;
      const {
        data,
        error
      } = await supabase.from('event_invites').select('display_name').eq('event_id', eventId);
      if (error) {
        console.error('Error fetching existing guests:', error);
        return;
      }
      setExistingNames(data.map(guest => guest.display_name.toLowerCase()));
    };
    fetchExistingGuests();
  }, [eventId]);
  const resetForm = () => {
    setFirstName("");
  };
  const checkDuplicateName = (name: string) => {
    const normalizedName = name.toLowerCase();
    return existingNames.includes(normalizedName);
  };
  const handleAddGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) return;
    if (checkDuplicateName(firstName)) {
      setIsDuplicateModalOpen(true);
      return;
    }
    try {
      setIsSubmitting(true);

      // Directly add the guest to the event invites table
      const {
        error
      } = await supabase.from('event_invites').insert({
        event_id: eventId,
        display_name: firstName.trim()
      });
      if (error) throw error;

      // Update the existingNames array for future duplicate checks
      setExistingNames([...existingNames, firstName.toLowerCase()]);

      // Invalidate the event invites query to trigger a refetch
      queryClient.invalidateQueries({
        queryKey: ['event-invites', eventId]
      });
      toast({
        title: "Success",
        description: `Successfully invited ${firstName}`
      });
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Error inviting guest:', error);
      toast({
        title: "Error",
        description: "Failed to invite guest. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="space-y-4">
      <form onSubmit={handleAddGuest} className="space-y-4">
        <div className="space-y-2">
          
          <div className="flex gap-2">
            <Input id="guestName" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="i.e. John Doe" className="flex-1 bg-white" />
            <Button type="submit" variant="secondary" className="bg-white hover:bg-gray-100" disabled={!firstName.trim() || isSubmitting}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </form>

      <DuplicateNameDialog isOpen={isDuplicateModalOpen} onClose={() => setIsDuplicateModalOpen(false)} />
    </div>;
};